import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition — wraps routed page content and replays a subtle entrance
 * animation (fade in + a small upward slide) every time the URL path changes,
 * giving smooth transitions between pages.
 *
 * How it works: the wrapper is keyed by the current pathname, so React remounts
 * it on navigation and the CSS entrance animation runs again. Pure CSS (MUI
 * keyframes) — no animation library dependency.
 *
 * Accessibility: honors `prefers-reduced-motion: reduce` by disabling the
 * animation for users who opt out of motion.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <Box
      key={pathname}
      sx={{
        '@keyframes mstmsPageIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'mstmsPageIn 340ms cubic-bezier(0.22, 1, 0.36, 1) both',
        willChange: 'opacity, transform',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {children}
    </Box>
  );
}
